import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Chip,
  IconButton,
  Divider,
  Avatar,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import InventoryIcon from "@mui/icons-material/Inventory"
import PersonIcon from "@mui/icons-material/Person"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import CustomerSelect from "pages/Orders/customersmodal"
import { postTask, postLoan } from "helpers/api_helper"

const LoanModalCust = ({ fetchLoansData, isOpen, customers, setLoanModal, loans, order = {} }) => {
  const { t } = useTranslation()
  const [customerid, setCustomerid] = useState("")
  const [customername, setCustomername] = useState("")
  const [selectedCustomerLoans, setSelectedCustomerLoans] = useState([])
  const [selectedProducts, setSelectedProducts] = useState([])
  const [returnedQuantities, setReturnedQuantities] = useState({})

  useEffect(() => {
    if (isOpen) {
      setSelectedProducts([])
      setReturnedQuantities({})
      setCustomerid("")
      setCustomername("")
      setSelectedCustomerLoans([])

      if (order?.customer_id) {
        setCustomerid(order.customer_id)
        setCustomername(order.name)
        filterLoans(order.customer_id)
      }
    }
  }, [isOpen, order])

  const filterLoans = (custId) => {
    const filteredLoans = loans
      .filter((loan) => loan.customer_id === custId)
      .reduce((acc, loan) => {
        const { order_id, prdname, prd_id, quantity, custname, bound, customer_id } = loan
        const existingProduct = acc.find((item) => item.prd_id === prd_id)

        if (existingProduct) {
          if (bound === "OUTBOUND") {
            existingProduct.outbound += quantity
          } else if (bound === "INBOUND") {
            existingProduct.inbound += quantity
          }
          existingProduct.total = existingProduct.outbound - existingProduct.inbound
        } else {
          acc.push({
            customer_id,
            order_id,
            prdname,
            custname,
            prd_id,
            inbound: bound === "INBOUND" ? quantity : 0,
            outbound: bound === "OUTBOUND" ? quantity : 0,
            total: bound === "OUTBOUND" ? quantity : -quantity,
          })
        }
        return acc
      }, [])

    setSelectedCustomerLoans(filteredLoans)
  }

  const handleCustomerSelect = (selectedCustomerId) => {
    if (!selectedCustomerId) return
    setCustomerid(selectedCustomerId)
    const selectedCustomer = customers.find((cust) => cust.id === selectedCustomerId)
    setCustomername(selectedCustomer ? selectedCustomer.name : "")
    filterLoans(selectedCustomerId)
  }

  const handleProductSelect = (product) => {
    if (!selectedProducts.some((p) => p.prd_id === product.prd_id)) {
      setSelectedProducts([...selectedProducts, product])
    }
  }

  const handleQuantityChange = (prd_id, quantity) => {
    setReturnedQuantities((prev) => ({ ...prev, [prd_id]: quantity }))
  }

  const handleCreateReturns = async () => {
    for (const product of selectedProducts) {
      const returnedQuantity = returnedQuantities[product.prd_id]
      if (returnedQuantity && returnedQuantity > 0) {
        await createTaskAndLoan(product, returnedQuantity, customerid)
      }
    }
    setLoanModal(false)
  }

  const createTaskAndLoan = async (selectedProduct, returnedQuantity, customerid) => {
    const taskPayload = {
      resource: [
        {
          type: "ΠΛΥΝΤΗΡΙΟ",
          materials: "RETURN",
          status: "PENDING",
          orderref: selectedProduct.order_id,
          productid: selectedProduct.prd_id,
          name: selectedProduct.prdname,
          description: "EPISTROFH-PLYSIMO",
          quantity: parseInt(returnedQuantity),
          customer_id_ref: customerid,
          customer_name: selectedProduct.custname,
          seq: 1,
        },
      ],
    }

    try {
      const taskResponse = await postTask(taskPayload)
      if (taskResponse && taskResponse.resource) {
        const loanPayload = {
          resource: [
            {
              bound: "INBOUND",
              quantity: returnedQuantity,
              customer_id: customerid,
              order_id: selectedProduct.order_id,
              prd_id: selectedProduct.prd_id,
              prdname: selectedProduct.prdname,
              custname: selectedProduct.custname,
            },
          ],
        }
        await postLoan(loanPayload)
      }
    } catch (error) {
      console.error("Error in task or loan creation:", error)
    }
  }

  const handleModalClose = () => {
    fetchLoansData()
    setLoanModal(false)
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e2e8f0",
          bgcolor: "#f8fafc",
          py: 2,
          px: 3,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <InventoryIcon sx={{ color: "#3b82f6" }} />
            <Typography variant="h6" fontWeight={600} color="#1e293b">
              {t("Create Inbound Return")}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={handleModalClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Customer Info or Selection */}
          {customername ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: "#16a34a", width: 36, height: 36 }}>
                  <PersonIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="#166534">
                    {t("Customer")}
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="#166534">
                    {customername}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          ) : (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1 }}>
                {t("Select Customer")}
              </Typography>
              <CustomerSelect customers={customers} onSelect={handleCustomerSelect} />
            </Box>
          )}

          {/* Loaned Items List */}
          {selectedCustomerLoans.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1.5 }}>
                {t("Customer Loaned Items")}
              </Typography>
              <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}>
                <List disablePadding sx={{ maxHeight: 200, overflow: "auto" }}>
                  {selectedCustomerLoans.map((loan, index) => (
                    <React.Fragment key={loan.prd_id}>
                      {index > 0 && <Divider />}
                      <ListItemButton
                        onClick={() => handleProductSelect(loan)}
                        sx={{
                          py: 1.5,
                          "&:hover": { bgcolor: "#f8fafc" },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body2" fontWeight={500}>
                                {loan.prdname}
                              </Typography>
                              <Chip
                                label={`${t("Loaned")}: ${loan.total}`}
                                size="small"
                                color={loan.total > 0 ? "warning" : "success"}
                                sx={{ height: 22, fontSize: "0.75rem" }}
                              />
                            </Stack>
                          }
                          secondary={
                            <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="#64748b">
                                {t("Inbound")}: {loan.inbound}
                              </Typography>
                              <Typography variant="caption" color="#64748b">
                                {t("Outbound")}: {loan.outbound}
                              </Typography>
                            </Stack>
                          }
                        />
                        <AddCircleIcon sx={{ color: "#3b82f6", fontSize: 20 }} />
                      </ListItemButton>
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </Box>
          )}

          {/* No Loans Message */}
          {customerid && selectedCustomerLoans.length === 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                bgcolor: "#fefce8",
                border: "1px solid #fde047",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="#854d0e">
                {t("No loaned items found for this customer")}
              </Typography>
            </Paper>
          )}

          {/* Selected Products with Quantity Input */}
          {selectedProducts.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1.5 }}>
                {t("Return Quantities")}
              </Typography>
              <Stack spacing={2}>
                {selectedProducts.map((product) => (
                  <Paper
                    key={product.prd_id}
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid #e2e8f0",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" fontWeight={500} color="#1e293b" sx={{ mb: 1 }}>
                      {product.prdname}
                    </Typography>
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      placeholder={t("Enter return quantity")}
                      value={returnedQuantities[product.prd_id] || ""}
                      onChange={(e) => handleQuantityChange(product.prd_id, e.target.value)}
                      inputProps={{ min: 0 }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "#fff",
                        },
                      }}
                    />
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
        <Button onClick={handleModalClose} sx={{ color: "#64748b" }}>
          {t("Close")}
        </Button>
        {selectedProducts.length > 0 && (
          <Button variant="contained" onClick={handleCreateReturns} startIcon={<InventoryIcon />}>
            {t("Create Returns")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

LoanModalCust.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  customers: PropTypes.array.isRequired,
  setLoanModal: PropTypes.func.isRequired,
  loans: PropTypes.array.isRequired,
  fetchLoansData: PropTypes.func.isRequired,
  order: PropTypes.object,
}

export default LoanModalCust
